import { useEffect, useRef, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import { contains } from '@open-tender/utils'
import {
  selectCartQuantity,
  selectModal,
  selectSidebar,
  selectSidebarModal,
  toggleSidebar,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import SidebarOverlay from './SidebarOverlay'
import SidebarContent from './SidebarContent'

const Sidebar = () => {
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const sidebarRef = useRef<HTMLElement | null>(null)
  const [active, setActive] = useState<Element | null>(null)
  const [elements, setElements] = useState<Element[]>([])
  const { type } = useAppSelector(selectModal)
  const { isOpen: sidebarModalOpen } = useAppSelector(selectSidebarModal)
  const { isOpen } = useAppSelector(selectSidebar)
  const cartQuantity = useAppSelector(selectCartQuantity)
  const showEmptyCart = contains(pathname, ['menu', 'checkout'])
  const hideCart =
    (cartQuantity === 0 && !showEmptyCart) ||
    contains(pathname, ['review', 'gift-cards'])
  const canToggle = !type && !hideCart && !sidebarModalOpen

  const handleExit = () => {
    if (active) (active as any).focus()
  }

  const handleFocus = () => {
    setActive(document.activeElement)
    const allElements = sidebarRef?.current?.querySelectorAll(
      'a[href], button, input, select, textarea'
    )
    let allElementsArr = Array.from(allElements ?? [])
    setElements(allElementsArr)
    const activeElements = Array.from(allElementsArr).filter(
      (i: any) => !i.disabled
    )
    const lastElement = activeElements
      ? activeElements[activeElements.length - 1]
      : null
    if (lastElement) (lastElement as any).focus()
  }

  const handleKeys = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.keyCode === 9 && sidebarRef.current && elements.length) {
        const activeElements = Array.from(elements).filter(
          (i: any) => !i.disabled
        )
        const firstElement = activeElements[0]
        const lastElement = activeElements[activeElements.length - 1]

        if (!evt.shiftKey && document.activeElement === lastElement) {
          ;(firstElement as any).focus()
          evt.preventDefault()
        }

        if (evt.shiftKey && document.activeElement === firstElement) {
          ;(lastElement as any).focus()
          evt.preventDefault()
        }
      } else if (evt.keyCode === 27 && canToggle) {
        dispatch(toggleSidebar())
      }
    },
    [elements, canToggle, dispatch]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeys, false)
    return () => document.removeEventListener('keydown', handleKeys, false)
  }, [handleKeys])

  return (
    <>
      <SidebarOverlay />
      <TransitionGroup component={null}>
        {isOpen ? (
          <CSSTransition
            key="sidebar"
            classNames="sidebar"
            timeout={{ enter: 250, exit: 250 }}
            onEntered={handleFocus}
            onExited={handleExit}
          >
            <SidebarContent ref={sidebarRef} />
          </CSSTransition>
        ) : null}
      </TransitionGroup>
    </>
  )
}

export default Sidebar
